import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

interface UseContactDetailQuickHelpOptions {
    enabled?: boolean
}

/**
 * Quick help options for Contact Detail Modal
 * Full workflow guide for handling leads from Marketing
 */
export const useContactDetailQuickHelp = ({
    enabled = true,
}: UseContactDetailQuickHelpOptions = {}) => {
    const options: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'contact-detail-workflow',
                label: 'Hướng dẫn xử lý Lead',
                description: 'Quy trình từ A-Z xử lý lead Marketing',
                tourTitle: 'Quy trình xử lý Lead',
                tourDescription:
                    'Hướng dẫn đầy đủ các bước xử lý lead từ lúc nhận về đến khi chuyển thành khách hàng.',
                tourSteps: [
                    {
                        selector: '.lead-conversion-flow',
                        title: '1. Lead mới đổ về',
                        content:
                            'Khi có contact mới từ Marketing (form, livechat, hotline...), lead sẽ hiển thị ở đây với trạng thái UNCLASSIFIED và chỉ có thông tin cơ bản.',
                    },
                    {
                        selector: '[data-tour="assign-to-me"]',
                        title: '2. Nhận Lead (Assign to me)',
                        content:
                            'Deadline: 1 ngày từ khi contact đổ về\n\nSales trực hôm đó cần bấm "Assign to me" để nhận Lead. Nếu quá hạn sẽ hiển thị cảnh báo màu đỏ.',
                    },
                    {
                        selector: '.contact-form-fields',
                        title: '3. Cập nhật thông tin',
                        content:
                            'Deadline: 2 ngày từ khi nhận Lead\n\nCập nhật đầy đủ thông tin: Tên, SĐT, Email, Công ty, Ngành nghề, Dịch vụ quan tâm, Ghi chú...',
                    },
                    {
                        selector: '[data-tour="classify-buttons"]',
                        title: '4. Phân loại Lead',
                        content:
                            'Sau khi cập nhật thông tin, phân loại Lead:\n\n• Warm Lead (MQL): Quan tâm dịch vụ VNETWORK\n• SQL: Có đủ BANT, sẵn sàng mua\n• Cool Lead: Chưa có nhu cầu ngay\n• Unqualified: Không phù hợp',
                    },
                    {
                        selector: '[data-tour="view-customer"]',
                        title: '5. Tạo Lead bên Khách hàng',
                        content:
                            'Nếu phân loại là Warm/SQL/Cool Lead → Hệ thống tự động tạo Lead tương ứng bên module Khách hàng.\n\nBấm "Xem khách hàng" để mở trang chi tiết.',
                    },
                    {
                        selector: '[data-tour="view-customer"]',
                        title: '6. Tiếp tục tương tác',
                        content:
                            'Từ giờ, vui lòng sử dụng thông tin và tương tác trên dữ liệu Khách hàng để:\n\n• Cập nhật trạng thái deal\n• Ghi nhận cuộc gọi/meeting\n• Theo dõi pipeline\n• Chốt hợp đồng',
                    },
                ],
            },
            {
                id: 'contact-detail-assign',
                label: 'Nhận Lead (Assign to me)',
                description: 'Cách nhận lead về để xử lý',
                selector: '[data-tour="assign-to-me"]',
                tourTitle: 'Nhận Lead',
                tourDescription:
                    'Bấm nút "Assign to me" để nhận lead về.\n\nDeadline: 1 ngày từ khi contact đổ về\n\nSau khi nhận:\n• Lead sẽ được gán cho bạn\n• Bạn có thể cập nhật thông tin\n• Phân loại lead theo BANT',
            },
            {
                id: 'contact-detail-classify',
                label: 'Phân loại Lead (BANT)',
                description: 'Cách phân loại Warm/SQL/Cool/Unqualified',
                selector: '[data-tour="classify-buttons"]',
                tourTitle: 'Phân loại Lead',
                tourDescription:
                    'Phân loại lead dựa trên tiêu chí BANT:\n\n• Budget: Khả năng chi trả\n• Authority: Quyền quyết định\n• Need: Nhu cầu sử dụng dịch vụ\n• Timeline: Thời gian triển khai\n\n• Warm Lead: Quan tâm, có Authority\n• SQL: Đủ BANT, sẵn sàng mua\n• Cool Lead: Chưa có nhu cầu rõ ràng\n• Unqualified: Không phù hợp tiêu chí',
            },
            {
                id: 'contact-detail-view-customer',
                label: 'Xem khách hàng',
                description: 'Chuyển sang trang chi tiết khách hàng',
                selector: '[data-tour="view-customer"]',
                tourTitle: 'Xem khách hàng',
                tourDescription:
                    'Sau khi phân loại lead (Warm/SQL/Cool), hệ thống tự động tạo Lead bên module Khách hàng.\n\nBấm "Xem khách hàng" để:\n• Mở trang chi tiết khách hàng\n• Tiếp tục tương tác và theo dõi\n• Cập nhật pipeline/deal\n• Ghi nhận activities',
            },
        ],
        []
    )

    // Only register options when enabled (modal is open)
    useQuickHelp(enabled ? options : [])

    return { options }
}

export default useContactDetailQuickHelp
